/* eslint-disable complexity, max-lines */
import { lensThemeOptions } from '@diligentcorp/atlas-theme-mui-lens';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from '@mui/material/styles';
import React, { Suspense } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { CompatRoute } from 'react-router-dom-v5-compat';
import {
  DataRetentionPage,
  DeletedSanitizedCases,
} from 'src/components/DataRetention/pages';
import { AuthorisedCaseImporter } from 'src/components/auth/AuthorisedCaseImporter';
import ResetPasswordConfirmation from 'src/components/login/screens/ResetPasswordConfirmation';
import SendActivationCodeEmail from 'src/components/login/screens/SendActivationCodeEmail';
import { NavbarPermissions } from 'src/components/organisms';
import { ReshubNavbar } from 'src/components/organisms/Navbar/ReshubNavbar';
import { ReshubNavbarAtlas } from 'src/components/organisms/Navbar/ReshubNavbarAtlas';
import { useAtlasNavPositioned } from 'src/components/organisms/Navbar/useAtlasNavPositioned';
import { useCanAccessDisclosures, useIsFeatureEnabled } from 'src/hooks';
import { useCanAccessSpeakUp } from 'src/hooks/useCanAccessSpeakUp';
import { AtlasMuiThemeProvider } from 'src/theme/atlas/atlasMuiThemeProvider';
import { AtlasToastProvider } from 'src/components/molecules/Toast';
import { analytics, generateRandomIPAddress } from 'src/utils/analytics';
import { boot } from 'src/utils/intercom';
import {
  AutoClosedDisclosureDetail,
  Case,
  CaseSettings,
  Cases,
  Categories,
  CoiDisclosures,
  CoiFormManagementPage,
  Configurations,
  CreateReport,
  CustomFields,
  DisclosureCaseDetail,
  EthicalHealthDataview,
  FormCreation,
  FormDetailPage,
  Groups,
  NonEmployeePassword,
  Settings,
  Tags,
  Users,
} from '../..';
import { fetchPublicSession } from '../../../../creators/OpenReporting.creators';
import { fetchSession } from '../../../../creators/User.creators';
import * as Integration from '../../../../data-integration/types';
import * as Types from '../../../../types';
import { FeatureFlag } from '../../../../types';
import FuzzyMatchPreview from 'src/components/admin/fuzzy-match-preview/FuzzyMatchPreview';
import { Faq } from '../../../Faq';
import AuthenticationAdmin from '../../../admin/authentication/AuthenticationAdmin';
import DataIntegration from '../../../admin/data-integration/DataIntegration';
import {
  AuthenticatedRoute,
  AuthorisedManager,
  RequiredFeatureEnabled,
  SecurityBoundary,
  isRequiredFeatureEnabled,
} from '../../../auth';
import { Center, Spinner } from '../../../atoms';
import ResolutionHubLogin from '../../../login/ResolutionHubLogin';
import ChangePasswordNew from '../../../login/screens/ChangePassword';
import { CreateSignUpPassword } from '../../../login/screens/CreateSignUpPassword';
import ResetPassword from '../../../login/screens/ResetPassword';
import SendResetPasswordEmail from '../../../login/screens/SendResetPasswordEmail';
import SSOErrorNew from '../../../login/screens/sso/SSOError';
import { ShortToken } from '../../../organismsLegacy/ShortToken/ShortToken';
import ValidateEmail from '../../../organismsLegacy/ValidateEmail';
import { CreateCase } from '../../CreateCase/CreateCase';
import { EthicsOperationsDataview } from '../../EthicsOperationsDataview';
import { InsightsDashboard } from '../../InsightsDashboard';
import { Routing } from '../../Routing';
import { UploadCases } from '../../UploadCases';
import { UploadCasesHistory } from '../../UploadCasesHistory/UploadCasesHistory';
import { UploadUsers } from '../../UploadUsers/UploadUsers';
import { PublicRouting } from '../PublicRouting/PublicRouting';
import { PublicRoutingBackground } from '../PublicRouting/PublicRouting.styles';
import { ReportingRouting } from '../ReportingRouting';
import { I18nDisabled } from '../components/I18nDisabled';
import {
  LoginWrapper,
  RoutingBody,
  RoutingContainer,
} from './InternalRouting.styles';

interface Props {
  location?: { pathname: string };
}

interface CasesRoutingProps {
  disableCasesUpload: boolean;
}

const lensTheme = createTheme(lensThemeOptions);

const CasesRouting = ({ disableCasesUpload }: CasesRoutingProps) => (
  <>
    <Route path="/cases" exact render={(_props) => <Cases />} />
    <Route
      path="/uploadCases"
      exact
      render={(_props) =>
        disableCasesUpload ? <Redirect to="/cases" /> : <UploadCases />
      }
    />
    <Route
      path="/uploadCasesHistory"
      exact
      render={(_props) =>
        disableCasesUpload ? <Redirect to="/cases" /> : <UploadCasesHistory />
      }
    />
    <Route path="/case-settings" exact render={() => <CaseSettings />} />
  </>
);

const DisclosuresRouting = () => (
  <>
    <Route path="/disclosures" exact render={() => <CoiDisclosures />} />
    <Route path="/disclosure" exact render={() => <DisclosureCaseDetail />} />
    <Route
      path="/auto-closed-disclosure"
      exact
      render={() => <AutoClosedDisclosureDetail />}
    />
    <Route
      path={`/disclosure/:id`}
      exact
      render={(renderProps) => {
        return (
          <Redirect to={`/disclosure?id=${renderProps.match.params.id}`} />
        );
      }}
    />
  </>
);

interface UsersRoutingProps {
  disableUserUpload: boolean;
  expiryPeriod: number;
}

const UsersRouting = ({
  disableUserUpload,
  expiryPeriod,
}: UsersRoutingProps) => (
  <>
    <Route path="/users" render={(_props) => <Users />} />

    <Route
      path="/uploadUsers"
      exact
      render={(_props) =>
        disableUserUpload ? (
          <Redirect to="/users" />
        ) : (
          <UploadUsers orgExpiryPeriod={expiryPeriod} />
        )
      }
    />
  </>
);

interface InsightsRoutingProps {
  organisation: Types.Organisation | null;
}

const InsightsRouting = ({ organisation }: InsightsRoutingProps) => (
  <RequiredFeatureEnabled requiredFeature={FeatureFlag.SPEAK_UP}>
    <Route path="/insights" exact render={(_props) => <InsightsDashboard />} />
    <Route
      path="/ethical-health"
      exact
      render={(_props) => <EthicalHealthDataview />}
    />
    <Route
      path="/ethics-operations"
      exact
      render={(..._props) => (
        <EthicsOperationsDataview organisation={organisation} />
      )}
    />
  </RequiredFeatureEnabled>
);

interface AdminRoutingProps {
  slug: string;
}

const AdminRouting = ({ slug }: AdminRoutingProps) => (
  <>
    <Route path="/groups" render={(_props) => <Groups />} />
    <RequiredFeatureEnabled requiredFeature={FeatureFlag.ROUTING}>
      <Route path="/routing" render={(_props) => <Routing />} />
    </RequiredFeatureEnabled>
    <Route
      path="/authentication"
      render={(_props) => <AuthenticationAdmin />}
    />
    <RequiredFeatureEnabled
      requiredFeature={[
        FeatureFlag.PUBLIC_REPORTING,
        FeatureFlag.VAULT_TALK,
        FeatureFlag.HRIS_SYNC,
        FeatureFlag.OPR_DEFAULT_ASSIGNEE_ROUTING,
        FeatureFlag.SLACK_REPORTING,
      ]}
    >
      <Route
        path="/configurations"
        render={() => <Configurations slug={slug} />}
      />
    </RequiredFeatureEnabled>
    <RequiredFeatureEnabled requiredFeature={FeatureFlag.DATA_INTEGRATION}>
      <Route path="/integrations" render={(_props) => <DataIntegration />} />
    </RequiredFeatureEnabled>
    <RequiredFeatureEnabled
      requiredFeature={FeatureFlag.FUZZY_MATCHING_NAMED_OFFENDERS}
    >
      <Route
        path="/fuzzy-match-preview"
        exact
        render={() => <FuzzyMatchPreview />}
      />
    </RequiredFeatureEnabled>
    <Route path="/categories" render={(_props) => <Categories />} />
    <RequiredFeatureEnabled
      requiredFeature={[
        FeatureFlag.DYNAMIC_QUESTIONS_V1,
        FeatureFlag.CONFLICT_OF_INTEREST,
      ]}
    >
      <AtlasMuiThemeProvider>
        <Route
          path="/form-management/:type/form/:formId"
          render={(_props) => <FormDetailPage />}
        />
        <Route
          path="/form-management/:type?"
          exact
          render={(_props) => {
            return <CoiFormManagementPage />;
          }}
        />
      </AtlasMuiThemeProvider>
    </RequiredFeatureEnabled>
    <Route path="/tags" render={(_props) => <Tags />} />
    <RequiredFeatureEnabled requiredFeature={FeatureFlag.CUSTOM_FIELDS}>
      <AtlasMuiThemeProvider>
        <Route
          path="/field-management"
          exact
          render={(_props) => <CustomFields />}
        />
      </AtlasMuiThemeProvider>
    </RequiredFeatureEnabled>
  </>
);

const DataRetentionRouting = () => (
  <RequiredFeatureEnabled requiredFeature={FeatureFlag.DATA_RETENTION}>
    <MuiThemeProvider theme={lensTheme}>
      <Route
        path="/data-retention"
        exact
        render={() => <DataRetentionPage />}
      />
    </MuiThemeProvider>
  </RequiredFeatureEnabled>
);

const DeletedSanitizedCasesRouting = () => (
  <RequiredFeatureEnabled requiredFeature={FeatureFlag.DATA_RETENTION}>
    <Route
      path="/deleted-sanitized-cases"
      exact
      render={() => <DeletedSanitizedCases />}
    />
  </RequiredFeatureEnabled>
);

export const InternalRouting: React.FC<Props> = (props) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const state = useSelector(
    (state: Types.State) => ({
      currentUser: state.globalUsers?.currentUser as Types.User,
      session: state.globalUsers.session,
      hash: state?.globalUsers?.hash,
      organisation: state.organisation?.organisation,
      authenticationType: state?.organisation?.organisation?.authenticationType,
      fetching: state?.fetching?.isFetching ?? true,
      features: state?.features,
      dataIntegration:
        state?.integrationStatus?.dataIntegration ?? Integration.Types.NONE,
      activeScimProvider: state?.organisation?.organisation?.activeScimProvider,
      isLegacyMode: state?.organisation?.isLegacyMode,
    }),
    shallowEqual,
  );

  const canAccessDisclosures = useCanAccessDisclosures();
  const canAccessSpeakUp = useCanAccessSpeakUp();
  const isAtlasGlobalNavEnabled = useIsFeatureEnabled(
    Types.FeatureFlag.ATLAS_GLOBAL_NAV,
  );
  // d1pHomeUrl is a proxy for "the D1P shell (which registers
  // <hb-global-navigator>) is present" — !isLegacyMode alone isn't enough.
  const isAtlasNavVariant =
    isAtlasGlobalNavEnabled &&
    !state.isLegacyMode &&
    !!state.organisation?.d1pHomeUrl;
  const isAtlasNavPositioned = useAtlasNavPositioned(isAtlasNavVariant);
  const segmentEnabled = isRequiredFeatureEnabled(
    state.features,
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  React.useEffect(() => {
    if (segmentEnabled) {
      analytics?.page?.(
        location.pathname,
        {},
        { context: { ip: generateRandomIPAddress() } },
      );
    }
  }, [location.pathname, segmentEnabled]);

  React.useEffect(() => {
    const meetsSessionRequirements =
      state.currentUser?.id &&
      state.session &&
      state.hash &&
      state.organisation?.id;

    if (meetsSessionRequirements) {
      boot({
        hide_default_launcher: true,
        user_id: String(state.currentUser.id),
        user_hash: state.hash,
        email: state.currentUser.email,
        company: {
          companyId: state.organisation!.id,
          name: state.organisation?.name || '',
        },
      });
    } else {
      boot({});
    }
  }, [state]);

  const disableUserUpload =
    state.activeScimProvider === true ||
    state.dataIntegration !== Integration.Types.NONE;

  const isCaseImporterEnabled = AuthorisedCaseImporter();

  const isLoggedIn = useSelector(
    (state: Types.State) => !!state.globalUsers?.currentUser,
  );

  const adminIsRoot = useSelector(
    (state: Types.State) =>
      state.globalUsers?.currentUser?.systemPermissions?.length === 1 &&
      state.globalUsers?.currentUser?.systemPermissions[0] ===
        NavbarPermissions.NAV_USERS,
  );

  const hasNavAdmin = useSelector((state: Types.State) =>
    state.globalUsers?.currentUser?.systemPermissions?.includes(
      NavbarPermissions.NAV_ADMIN,
    ),
  );

  return (
    <AtlasToastProvider>
      <Switch>
        {!isLoggedIn && (
          <Route path="/" exact render={() => <Redirect to="/login" />} />
        )}

        <Route
          path="/:organisation?/open-reporting"
          render={({
            match: {
              params: { organisation },
            },
          }) => {
            if (organisation === undefined) {
              [organisation] = document.location.hostname.split('.');
            }

            return (
              <SecurityBoundary
                fetching={state.fetching}
                fetchSession={() => fetchPublicSession()(dispatch)}
              >
                <PublicRoutingBackground>
                  <PublicRouting organisation={organisation} />
                </PublicRoutingBackground>
              </SecurityBoundary>
            );
          }}
        />

        <Route
          path="/oauth/error"
          render={(_props) => (
            <LoginWrapper>
              <SSOErrorNew />
            </LoginWrapper>
          )}
        />

        <Route
          path="/saml/error"
          render={(_props) => (
            <LoginWrapper>
              <SSOErrorNew />
            </LoginWrapper>
          )}
        />

        <Route
          path="/signup"
          render={(props) => (
            <LoginWrapper>
              <CreateSignUpPassword
                token={props.location.pathname.split('/')[2]}
              />
            </LoginWrapper>
          )}
        />

        <Route
          path="/reset/update-password/:id"
          exact
          render={(props) => (
            <LoginWrapper>
              <ResetPassword
                token={props.match.params.id}
                isEnforceBanner={true}
              />
            </LoginWrapper>
          )}
        />

        <Route
          path="/reset/app/:id"
          exact
          render={(props) => (
            <LoginWrapper isAppAccount={true}>
              <ResetPassword
                token={props.match.params.id}
                kind={Types.AccountType.App}
              />
            </LoginWrapper>
          )}
        />

        <Route
          path="/reset/:id"
          exact
          render={(props) => (
            <LoginWrapper>
              <ResetPassword token={props.match.params.id} />
            </LoginWrapper>
          )}
        />

        <Route
          path="/reset-confirmation"
          exact
          render={() => (
            <LoginWrapper isAppAccount={true}>
              <ResetPasswordConfirmation />
            </LoginWrapper>
          )}
        />

        <Route
          path="/resend"
          render={(_props) => (
            <LoginWrapper isAppAccount={true}>
              <SendActivationCodeEmail isAppAccount={true} />
            </LoginWrapper>
          )}
        />

        <Route
          path="/code"
          render={(props) => (
            <LoginWrapper isAppAccount={true}>
              <ShortToken token={props.location.pathname.split('/')[2]} />
            </LoginWrapper>
          )}
        />

        <Route
          path="/validate"
          render={(props) => (
            <ValidateEmail token={props.location.pathname.split('/')[2]} />
          )}
        />

        <Route
          path="/login"
          exact
          render={(_props) => (
            <LoginWrapper>
              <ResolutionHubLogin
                expired={false}
                segmentTracking={segmentEnabled}
              />
            </LoginWrapper>
          )}
        />

        <Route
          path="/expired"
          exact
          render={(_props) => (
            <LoginWrapper>
              <ResolutionHubLogin expired segmentTracking={segmentEnabled} />
            </LoginWrapper>
          )}
        />

        <Route
          path="/reset"
          render={(_props) => (
            <LoginWrapper>
              <SendResetPasswordEmail />
            </LoginWrapper>
          )}
        />

        <CompatRoute path="/reporting" render={() => <ReportingRouting />} />

        <Route
          path="/register/:organisation/:token"
          render={(_props) => (
            <SecurityBoundary
              fetching={state.fetching}
              fetchSession={() => fetchPublicSession()(dispatch)}
            >
              <PublicRoutingBackground>
                <NonEmployeePassword
                  slug={props.location?.pathname.split('/')[2] ?? ''}
                  token={props.location?.pathname.split('/')[3] ?? ''}
                />
              </PublicRoutingBackground>
            </SecurityBoundary>
          )}
        />

        <SecurityBoundary
          fetching={state.fetching}
          fetchSession={() => fetchSession()(dispatch)}
        >
          <I18nDisabled>
            <Route>
              <AuthenticatedRoute>
                {/* The nav renders unconditionally — it's what applies the
                  content-shifting body class in the first place (see
                  useAtlasNavPositioned), so hiding it here would deadlock
                  the gate below. Only the *content* inside RoutingBody
                  waits: on the Atlas variant, the nav's own Shadow DOM
                  render (even its lazy dev-mock chunk resolving) doesn't
                  guarantee that margin is applied yet, so rendering routes
                  beforehand flashes them in unpositioned under the sidebar.
                  Reuses SecurityBoundary's spinner so there's no visual
                  jump. No-op for the legacy variant. */}
                {/* HbGlobalNavigatorShell's dev-mode lazy mock has no local
                  Suspense boundary by design (see its own comment) — this is
                  the shared boundary it relies on, covering the nav and its
                  RoutingBody sibling together so neither commits before the
                  other while the dynamic import is pending. */}
                <Suspense
                  fallback={
                    <Center>
                      <Spinner variant="secondary" size="xlarge" />
                    </Center>
                  }
                >
                  <RoutingContainer $isAtlasVariant={isAtlasNavVariant}>
                    {isAtlasNavVariant ? (
                      <ReshubNavbarAtlas disableUpload={disableUserUpload} />
                    ) : (
                      <ReshubNavbar disableUpload={disableUserUpload} />
                    )}
                    <RoutingBody
                      moveLeft={false}
                      $isAtlasVariant={isAtlasNavVariant}
                      $suppressAtlasTransition={!isAtlasNavPositioned}
                      {...(isAtlasNavVariant
                        ? { 'data-atlas-gn-app': true }
                        : {})}
                    >
                      {!isAtlasNavPositioned ? (
                        <Center>
                          <Spinner variant="secondary" size="xlarge" />
                        </Center>
                      ) : (
                        <>
                          {adminIsRoot && (
                            <Route
                              path="/"
                              exact
                              render={(_props) => <Redirect to="/users" />}
                            />
                          )}
                          {!adminIsRoot && (
                            <Route
                              path="/"
                              exact
                              render={(_props) => (
                                <Redirect
                                  to={
                                    canAccessSpeakUp ? '/cases' : '/disclosures'
                                  }
                                />
                              )}
                            />
                          )}
                          {hasNavAdmin && (
                            <>
                              <AdminRouting
                                slug={state.organisation?.slug ?? ''}
                              />
                              <DataRetentionRouting />
                            </>
                          )}
                          {state.currentUser?.systemPermissions?.includes(
                            NavbarPermissions.NAV_USERS,
                          ) && (
                            <UsersRouting
                              disableUserUpload={disableUserUpload}
                              expiryPeriod={
                                state.organisation?.expiryPeriod ?? 0
                              }
                            />
                          )}
                          {state.currentUser?.systemPermissions?.includes(
                            NavbarPermissions.NAV_INSIGHTS,
                          ) && (
                            <InsightsRouting
                              organisation={state.organisation}
                            />
                          )}
                          <MuiThemeProvider theme={lensTheme}>
                            {state.currentUser?.systemPermissions?.includes(
                              NavbarPermissions.NAV_CASES,
                            ) && (
                              <>
                                {canAccessSpeakUp ? (
                                  <CasesRouting
                                    disableCasesUpload={!isCaseImporterEnabled}
                                  />
                                ) : (
                                  <Route
                                    path="/cases"
                                    exact
                                    render={() => (
                                      <Redirect to="/disclosures" />
                                    )}
                                  />
                                )}
                                {hasNavAdmin && (
                                  <DeletedSanitizedCasesRouting />
                                )}
                              </>
                            )}
                            {canAccessDisclosures && <DisclosuresRouting />}
                            <Route path="/case" exact render={() => <Case />} />
                            <Route
                              path={`/case/:id`}
                              exact
                              render={(renderProps) => {
                                return (
                                  <Redirect
                                    to={`/case?id=${renderProps.match.params.id}`}
                                  />
                                );
                              }}
                            />
                            {canAccessSpeakUp && (
                              <AuthorisedManager>
                                <Route
                                  path="/create-report"
                                  render={(_props) =>
                                    state.isLegacyMode ? (
                                      <CreateReport />
                                    ) : (
                                      <CreateCase />
                                    )
                                  }
                                />
                                <Route
                                  path="/create-existing-report"
                                  render={(_props) =>
                                    state.isLegacyMode ? (
                                      <CreateReport />
                                    ) : (
                                      <CreateCase />
                                    )
                                  }
                                />
                              </AuthorisedManager>
                            )}
                          </MuiThemeProvider>
                          <Route
                            path="/settings"
                            exact
                            render={() => <Settings />}
                          />
                          <Route
                            path="/change-password"
                            exact
                            render={() => <ChangePasswordNew />}
                          />
                          <Route path="/faq" render={(_props) => <Faq />} />
                          <RequiredFeatureEnabled
                            requiredFeature={[
                              FeatureFlag.DYNAMIC_QUESTIONS_V1,
                              FeatureFlag.CONFLICT_OF_INTEREST,
                            ]}
                          >
                            <AtlasMuiThemeProvider>
                              <Route
                                path="/form-creation/:formType/:questionnaireId"
                                exact
                                render={() => <FormCreation />}
                              />
                              <Route
                                path="/form-creation/:formType"
                                exact
                                render={() => <FormCreation />}
                              />
                            </AtlasMuiThemeProvider>
                          </RequiredFeatureEnabled>
                        </>
                      )}
                    </RoutingBody>
                  </RoutingContainer>
                </Suspense>
              </AuthenticatedRoute>
            </Route>
          </I18nDisabled>
        </SecurityBoundary>
      </Switch>
    </AtlasToastProvider>
  );
};
